// src/oura/tasks/TasksLive.tsx
//
// Delivery > Tasks on the live routes (ourteam-frontend#4681, backend epic
// #6480): the client's Master Tracker read from Oura, the sheet linked from
// here when there is none, Read now, and changes written to the sheet. The
// sheet wins a conflict: the server answers with its value and the row shows it.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link2Icon, RefreshCwIcon, SheetIcon } from "lucide-react";
import { createTask, deleteTask, getTasks, linkSheet, patchTask, readSheetNow, type NewTask, type TaskChange, type TasksPayload } from "@service/aeo/tasks";
import { cn } from "../lib/cn";
import { agoText } from "../docs/format";
import { PillButton, TYPE } from "../ui";
import { useOuraRole } from "../role";
import { TASKS_WRITE_ROWS } from "./flag";
import { describeRefusal } from "./refusals";
import { taskFromApi, type TaskPhase } from "./model";
import { TasksView, type AddAnswer, type ChangeAnswer, type DeleteAnswer } from "./TasksView";

function phasesOf(p: TasksPayload): TaskPhase[] {
    return p.phases.map((ph) => ({ key: ph.key, name: ph.name, title: ph.name, sub: ph.sub, tasks: ph.tasks.map(taskFromApi) }));
}

export function TasksLive({ businessId, initialPhase, initialOpen }: { businessId: string; initialPhase?: string | null; initialOpen?: string | null }) {
    const [payload, setPayload] = useState<TasksPayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    // Bumped on every payload the server sends, so the view starts again from it.
    const [version, setVersion] = useState(0);

    const take = useCallback((p: TasksPayload) => {
        setPayload(p);
        setVersion((v) => v + 1);
    }, []);
    // Kept here, which stays mounted once the sheet is linked (the link card does not).
    const [linkNote, setLinkNote] = useState<string | null>(null);
    const takeLinked = useCallback(
        (p: TasksPayload) => {
            if (p.linked?.converted) setLinkNote("Oura made a Google Sheet copy of the .xlsx, beside it, and linked the copy. Work in the copy from now on.");
            take(p);
        },
        [take],
    );

    useEffect(() => {
        let gone = false;
        void getTasks(businessId).then((ans) => {
            if (gone) return;
            if (ans.ok) take(ans.data);
            else setLoadError(ans.message);
        });
        return () => {
            gone = true;
        };
    }, [businessId, take]);

    const onChange = useCallback(
        async (id: string, change: TaskChange): Promise<ChangeAnswer> => {
            const ans = await patchTask(businessId, id, change);
            if (ans.ok) return { ok: true, task: taskFromApi(ans.data) };
            return { ok: false, message: ans.message, field: ans.field, sheetValue: ans.sheetValue };
        },
        [businessId],
    );

    // Add and delete: staff only, and only with TASKS_WRITE_ROWS (the BE refuses merchants too).
    const role = useOuraRole();
    const writeRows = TASKS_WRITE_ROWS && role === "oura-staff";
    const onAdd = useCallback(
        async (tab: string, body: Omit<NewTask, "tab">): Promise<AddAnswer> => {
            const ans = await createTask(businessId, { tab, ...body });
            if (ans.ok) return { ok: true, task: taskFromApi(ans.data) };
            const said = describeRefusal(ans.code, ans.message);
            return { ok: false, message: said.message, code: ans.code, field: said.field };
        },
        [businessId],
    );
    const onDelete = useCallback(
        async (id: string): Promise<DeleteAnswer> => {
            const ans = await deleteTask(businessId, id);
            if (ans.ok) return { ok: true };
            const said = describeRefusal(ans.code, ans.message).message;
            // A row changed in the sheet is kept: say so, and show the task as the sheet has it now.
            return {
                ok: false,
                code: ans.code,
                message: ans.code === "sheet_won" ? `Nothing was deleted. ${said}` : said,
                task: ans.task ? taskFromApi(ans.task) : undefined,
            };
        },
        [businessId],
    );

    const onReadNow = useCallback(async (): Promise<string | null> => {
        const ans = await readSheetNow(businessId);
        if (!ans.ok) return ans.message;
        take(ans.data);
        return null;
    }, [businessId, take]);

    const changedInSheet = useMemo(() => {
        const out: Record<string, string> = {};
        for (const ph of payload?.phases ?? [])
            for (const t of ph.tasks) {
                const c = t.changedInSheet;
                if (c) out[t.id] = [c.by, agoText(c.at)].filter(Boolean).join(", ");
            }
        return out;
    }, [payload]);

    if (loadError) {
        return <p className={cn(TYPE.tb, "m-0 rounded-2xl bg-rose-50 px-4 py-3 text-rose-800 ring-1 ring-inset ring-rose-200")}>{loadError}</p>;
    }
    if (!payload) return <p className={cn(TYPE.tb, "m-0 px-1 text-gray-500")}>Reading the tasks…</p>;
    if (!payload.sheetUrl) return <LinkSheet businessId={businessId} onLinked={takeLinked} />;
    const phases = phasesOf(payload);
    const first = phases.some((p) => p.key === initialPhase) ? (initialPhase ?? undefined) : phases[0]?.key;
    return (
        <div className="space-y-4">
            {linkNote && (
                <p role="status" className={cn(TYPE.tb, "m-0 rounded-2xl bg-blue-50 px-4 py-3 text-blue-900 ring-1 ring-inset ring-blue-200")}>
                    {linkNote}
                </p>
            )}
            {phases.length ? (
                <TasksView
                    key={version}
                    phases={phases}
                    sheetUrl={payload.sheetUrl}
                    syncedAgo={agoText(payload.lastReadAt) ?? "not yet"}
                    changedInSheet={changedInSheet}
                    initialPhase={first}
                    initialOpen={initialOpen ?? null}
                    onChange={onChange}
                    onAdd={writeRows ? onAdd : undefined}
                    onDelete={writeRows ? onDelete : undefined}
                    onReadNow={onReadNow}
                    lastError={payload.lastError}
                    needsCode={payload.needsCode}
                />
            ) : (
                <EmptyTasks onReadNow={onReadNow} lastError={payload.lastError} />
            )}
        </div>
    );
}

/** A linked sheet with no task rows yet: Read now is here too, so the first rows can be loaded. */
function EmptyTasks({ onReadNow, lastError }: { onReadNow: () => Promise<string | null>; lastError: string | null }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const read = async () => {
        setBusy(true);
        setError(null);
        try {
            setError(await onReadNow());
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="rounded-2xl bg-white px-5 py-4 ring-1 ring-inset ring-black/[0.06]">
            <p className={cn(TYPE.tb, "m-0 text-gray-700")}>The linked sheet has no task rows yet. Add them in the sheet, then read it again.</p>
            <PillButton className="mt-3" icon={RefreshCwIcon} busy={busy} writes onClick={() => void read()}>
                Read now
            </PillButton>
            {(error ?? lastError) && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                    {error ?? lastError}
                </p>
            )}
        </div>
    );
}

function LinkSheet({ businessId, onLinked }: { businessId: string; onLinked: (p: TasksPayload) => void }) {
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const link = async () => {
        setBusy(true);
        setError(null);
        const ans = await linkSheet(businessId, url.trim());
        setBusy(false);
        if (!ans.ok) {
            setError(ans.message);
            return;
        }
        onLinked(ans.data);
    };

    return (
        <div className="rounded-2xl bg-white px-5 py-4 ring-1 ring-inset ring-black/[0.06]">
            <div className="flex items-start gap-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-green-50 text-green-700">
                    <SheetIcon size={15} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tt, "m-0 text-gray-900")}>Link the Master Tracker</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>
                        Paste the client's tracker: a Google Sheet, or the .xlsx in Drive. Oura reads its task rows and adds a hidden Oura ID column; nothing else in the sheet changes.
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <input
                            aria-label="Tracker link"
                            className={cn(TYPE.tb, "min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-1.5 focus:border-blue-400 focus:outline-none")}
                            placeholder="https://docs.google.com/spreadsheets/d/…"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                        />
                        <PillButton variant="primary" icon={Link2Icon} busy={busy} writes disabled={!url.trim()} onClick={() => void link()}>
                            Link
                        </PillButton>
                    </div>
                    {error && (
                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                            {error}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
